import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Button, Typography } from '@one-thd/sui-atomic-components';
import { QuickView } from '@thd-olt-component-react/quick-view';
import { OrderSample } from '@thd-olt-component-react/order-sample';
import {
  QueryProvider,
  useDataModel,
  extend,
  string,
  shape,
  params,
  arrayOf
} from '@thd-nucleus/data-sources';
import keyBy from 'lodash/keyBy';
import { TConfiguratorOption } from '../_propTypes';
import {
  getPricing,
  updateSkuData,
  getPadChoicesFromHDMS,
  setPageError,
  clearPageError
} from '../../actions';
import {
  carpetByChoiceIdAndSkuSelector,
  getSkuMatchedChoicesSelector,
  selectSelectedSkuData
} from '../../selectors/skuDataSelectors';
import { selectSelectedCarpetChoice } from '../../selectors/pricing';
import {
  Divider,
  FlooringCard,
  SuperSkuContainer,
  SuperSkuDetails,
  SuperSkuDetailsSkeleton,
  SuperSkuFooter,
  SuperSkuImg,
  SuperSkuImgSkeleton,
  SuperSkuLinks,
  SuperSkuOptions,
  SuperSkuPrice
} from './styles';
import { trackCarpetSelectionQuickView } from '../../utils/analytics/trackEvents';
import { getHdmsStoreDetails } from '../../selectors/thdStoreInfo';
import {
  APP_ACTION_GRAPH_PRODUCT_CALL_FAILED,
  logNewRelicError
} from '../../utils/newRelicActions';

const PLPUrl =
  'https://www.homedepot.com/b/Flooring-Carpet-Installed-Carpet/N-5yc1vZ2fkpcfz';
const CARTURL = 'https://www.homedepot.com/mycart/home';

const SuperSkuTemplate = ({ renderOptions, options }) => {
  const dispatch = useDispatch();
  const [showQuickView, setShowQuickView] = useState(false);
  const [fetch, setFetch] = useState(false);
  const [showSkeleton, setShowSkeleton] = useState(true);
  const [itemId, setItemId] = useState(false);

  const carpetChoicesByIdAndSku = useSelector(carpetByChoiceIdAndSkuSelector);
  const skuMatchedChoices = useSelector(getSkuMatchedChoicesSelector);
  const selectedChoice = useSelector(selectSelectedCarpetChoice);
  const storeDetails = useSelector(getHdmsStoreDetails);
  const selectedSkuData = useSelector(selectSelectedSkuData);

  const { priceSquareFoot } = selectedChoice || {};
  const {
    data: productData,
    error,
    loading
  } = useDataModel('product', {
    skip: !selectedSkuData?.sku || selectedSkuData.productLabel,
    variables: { itemId: selectedSkuData.sku }
  });

  useEffect(() => {
    if (error && !loading) {
      logNewRelicError(
        APP_ACTION_GRAPH_PRODUCT_CALL_FAILED,
        'graphQL',
        'graphQL',
        'graphQL',
        { message: error.message, sku: selectedSkuData?.sku }
      );
      dispatch(setPageError({ errorMsg: 'Missing Product Data' }));
    }
    if (!loading && productData) {
      dispatch(clearPageError);
      dispatch(updateSkuData(productData.product));
    }
    if (productData?.product?.itemId) {
      setItemId(productData?.product?.itemId);
    }
  }, [productData]);

  useEffect(() => {
    if (!selectedSkuData?.primaryImageUrl) {
      setShowSkeleton(true);
    }
    if (selectedSkuData?.hdmsSku) {
      const hdmsApiPayload = {
        storeId: `${storeDetails.storeNumber}`,
        searchText: selectedSkuData.hdmsSku,
        productType: 'SubProduct',
        filter: ['IC_Material_CPT_Pad']
      };
      dispatch(getPadChoicesFromHDMS(hdmsApiPayload));
    }
  }, [selectedSkuData]);

  useEffect(() => {
    const pricingPayload = {
      choicesWithSku: keyBy(skuMatchedChoices, 'id'),
      carpetChoicesByIdAndSku
    };
    dispatch(getPricing(pricingPayload));
  }, [storeDetails?.storeNumber]);

  const fetchQuickView = () => {
    setShowQuickView(true);
    setFetch(true);
    trackCarpetSelectionQuickView(selectedSkuData.sku);
  };

  return (
    <>
      <div className="flooring-card">
        <div id="supersku-header">
          <Typography variant="h2" className="heading">
            Carpet
          </Typography>
          <Typography
            paragraph
            className="description"
            style={{ padding: '16px 0' }}
          >
            Your carpet selection has been carried over and applied to your
            estimate.
          </Typography>
          <Typography variant="h4" className="title">
            My Carpet
          </Typography>
        </div>
        <div className="supersku-container">
          <div className="supersku-img-container">
            <div className="supersku-img">
              <img
                src={selectedSkuData?.primaryImageUrl?.replace('<SIZE>', '400')}
                height="320"
                width="320"
                alt={selectedSkuData?.primaryImageAltText}
              />
            </div>
          </div>
          <div className="super-sku-details">
            <Typography paragraph>{selectedSkuData.brandName}</Typography>
            <Typography variant="h4">{selectedSkuData.productLabel}</Typography>
          </div>
          <div className="super-sku-price">
            <p className="price">${priceSquareFoot}&nbsp;</p>
            <span className="unit">/sq. ft.</span>
            <SuperSkuOptions>{renderOptions(options)}</SuperSkuOptions>
            <SuperSkuLinks>
              <Button variant="text" onClick={fetchQuickView}>
                View Details
              </Button>
              <QueryProvider cacheKey="order-sample">
                <OrderSample itemId={itemId} />
              </QueryProvider>
            </SuperSkuLinks>
          </div>
        </div>
        <Divider />
        <SuperSkuFooter>
          <div className="supersku-footer-content">
            <Typography variant="h3" height="loose">
              Looking for a different carpet?
            </Typography>
            <Typography paragraph>
              Explore more carpet options to find what works for your home.
            </Typography>
          </div>
          <div className="supersku-footer-action">
            <Button
              variant="secondary"
              fullWidth
              onClick={() => window.open(PLPUrl, '_blank')}
            >
              Explore More Carpets
            </Button>
          </div>
        </SuperSkuFooter>
        <QueryProvider cacheKey="quick-view">
          <QuickView
            isOpen={showQuickView}
            onClose={() => setShowQuickView(false)}
            itemId={selectedSkuData.sku}
            fetch={fetch}
          />
        </QueryProvider>
      </div>
    </>
  );
};

SuperSkuTemplate.propTypes = {
  options: PropTypes.objectOf(TConfiguratorOption).isRequired,
  renderOptions: PropTypes.func.isRequired
};

SuperSkuTemplate.dataModel = extend({
  product: params({
    itemId: string().isRequired()
  }).shape({
    itemId: string(),
    details: shape({
      collection: shape({
        name: string(),
        url: string()
      })
    }),
    identifiers: shape({
      canonicalUrl: string(),
      productType: string(),
      brandName: string(),
      productLabel: string(),
      specialOrderSku: string(),
      sampleId: string()
    }),
    media: shape({
      images: arrayOf(
        shape({
          url: string(),
          type: string(),
          subType: string(),
          sizes: arrayOf(string()),
          altText: string()
        })
      )
    })
  })
});

export default SuperSkuTemplate;
